import { Library, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTenant } from "@/contexts/TenantContext";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { paymentCallbackBreadcrumbs } from "@/pages/payment-callback-breadcrumbs";
import { PaymentCallbackActions } from "./payment-callback/PaymentCallbackActions";
import { PaymentStatusMark } from "./payment-callback/PaymentStatusMark";
import { paymentDetail, paymentHeadline, paymentNextSteps } from "./payment-callback/payment-copy";
import { usePaymentCallback } from "./payment-callback/usePaymentCallback";

function PaymentConfirmCard({ callback }: { callback: ReturnType<typeof usePaymentCallback> }) {
  const { tenant } = useTenant();
  const steps =
    callback.status === "success"
      ? paymentNextSteps(callback.registration, callback.thesis, callback.digitalResource)
      : [];
  const libraryName = tenant?.name?.trim() || "Library payment";

  return (
    <div className="payment-confirm-enter mx-auto flex w-full max-w-md flex-col items-center rounded-2xl border border-border bg-card px-6 py-10 text-center shadow-sm sm:px-10">
      {tenant?.logoUrl ? (
        <img
          src={tenant.logoUrl}
          alt=""
          className="mb-6 h-12 w-auto max-w-[180px] object-contain"
        />
      ) : (
        <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Library className="h-6 w-6" aria-hidden />
        </div>
      )}
      <p className="text-sm font-medium text-muted-foreground">{libraryName}</p>
      <div className="mt-8">
        <PaymentStatusMark status={callback.status} />
      </div>
      <h1 className="font-heading mt-6 text-3xl font-bold tracking-tight text-foreground">
        {paymentHeadline(callback.status)}
      </h1>
      <p className="mt-3 text-base leading-relaxed text-muted-foreground">
        {paymentDetail({
          status: callback.status,
          hasReference: Boolean(callback.reference),
          registration: callback.registration,
          thesis: callback.thesis,
          digitalResource: callback.digitalResource,
        })}
      </p>
      {callback.reference ? (
        <p className="mt-6 w-full rounded-lg bg-muted px-4 py-3 text-left">
          <span className="block text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Reference
          </span>
          <span className="mt-1 block break-all font-mono text-sm text-foreground">
            {callback.reference}
          </span>
        </p>
      ) : null}
      {steps.length > 0 ? (
        <ul className="mt-6 w-full space-y-2 text-left text-sm text-foreground">
          {steps.map((step) => (
            <li key={step} className="flex gap-2">
              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden />
              <span>{step}</span>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="mt-8 w-full">
        <PaymentCallbackActions
          status={callback.status}
          invoiceId={callback.invoiceId}
          portalBaseUrl={callback.portalBaseUrl}
          digitalResource={callback.digitalResource}
          digitalResourceId={callback.digitalResourceId}
        />
      </div>
    </div>
  );
}

export default function PaymentCallbackPage() {
  const { user, isLoading } = useAuth();
  const callback = usePaymentCallback();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted">
        <Loader2 className="h-8 w-8 animate-spin text-primary" aria-label="Loading" />
      </div>
    );
  }

  if (user) {
    return (
      <AppLayout>
        <PageHeader
          title="Payment confirmation"
          description="Secure transaction status"
          showSearch={false}
          breadcrumbItems={paymentCallbackBreadcrumbs(
            callback.invoiceType,
            callback.digitalResourceId
          )}
        />
        <div className="px-4 py-8 sm:px-6">
          <PaymentConfirmCard callback={callback} />
        </div>
      </AppLayout>
    );
  }

  return (
    <main className="min-h-screen bg-muted px-4 py-10 sm:px-6 sm:py-16">
      <PaymentConfirmCard callback={callback} />
    </main>
  );
}
